import React from 'react';
import {
  MATCH_VOTE_ICON_SVG,
  RARITY_ICON_SVG
} from './helpIconTokens';
import { getAugmentDisplaySvg, getAugmentDisplayName } from '../utils/augments';
import {
  OUTCOME_ICON_SVG,
  EXACT_ICON_SVG,
  buildMatchPointsBreakdown
} from '../utils/matchPointsBreakdown';
import './PointsBreakdown.css';

function iconSrc(icon, inventory) {
  if (!icon) return null;
  if (icon.type === 'outcome') return OUTCOME_ICON_SVG;
  if (icon.type === 'exact') return EXACT_ICON_SVG;
  if (icon.type === 'consensus') return RARITY_ICON_SVG;
  if (icon.type === 'vote') return MATCH_VOTE_ICON_SVG;
  if (icon.type === 'augment') {
    return getAugmentDisplaySvg(icon.ref || icon.code, inventory) ?? null;
  }
  return null;
}

function iconAlt(icon) {
  if (!icon) return '';
  if (icon.type === 'outcome') return 'outcome';
  if (icon.type === 'exact') return 'exact score';
  if (icon.type === 'consensus') return 'rarity';
  if (icon.type === 'vote') return 'vote';
  if (icon.type === 'augment') return getAugmentDisplayName(icon.code) || icon.code;
  return '';
}

function InlineIcons({ icons, inventory }) {
  if (!icons?.length) return null;
  return icons.map((icon, index) => {
    const src = iconSrc(icon, inventory);
    if (!src) return null;
    return (
      <img
        key={`${icon.type}-${icon.code || icon.type}-${index}`}
        src={src}
        alt={iconAlt(icon)}
        className="points-breakdown-icon"
      />
    );
  });
}

function Term({ value, icons, inventory }) {
  return (
    <span className="points-breakdown-term">
      {value}
      <InlineIcons icons={icons} inventory={inventory} />
    </span>
  );
}

function formatMultiplierNumber(value) {
  const m = Number(value);
  if (!Number.isFinite(m) || m === 1) return '1';
  if (Number.isInteger(m)) return String(m);
  return m.toFixed(1);
}

function MultiplierTerm({ multiplier, inventory }) {
  if (!multiplier) return null;
  return (
    <span className="points-breakdown-term points-breakdown-term--mult">
      {formatMultiplierNumber(multiplier.value)}
      <InlineIcons icons={multiplier.icons} inventory={inventory} />
    </span>
  );
}

/**
 * Inline explanation of how a finished pick's points were calculated.
 */
function PointsBreakdown({
  predictedHome,
  predictedAway,
  game,
  augment,
  pickStats,
  playerVotedForMatch = false,
  expectedPoints = null,
  augmentApplied = null,
  augmentPoints = null,
  inventory
}) {
  const breakdown = buildMatchPointsBreakdown({
    predictedHome,
    predictedAway,
    game,
    augment,
    pickStats,
    playerVotedForMatch,
    expectedPoints,
    augmentApplied,
    augmentPoints
  });
  if (!breakdown) return null;

  let body = null;

  if (breakdown.mode === 'flat') {
    body = (
      <>
        <Term
          value={breakdown.flat.value}
          icons={breakdown.flat.icons}
          inventory={inventory}
        />
        {' = '}
        <span className="points-breakdown-total">{breakdown.total}</span>
      </>
    );
  } else {
    const { addends, postMultiplierAddends = [], multiplier } = breakdown;
    const hasMult = Boolean(multiplier) && Number(multiplier.value) !== 1;
    const hasPost = postMultiplierAddends.length > 0;
    // Rarity scales pre-mult terms only: (outcome × 1.7) + precision
    const wrapProduct = hasMult && hasPost;
    const wrapAddends =
      !wrapProduct && (addends.length > 1 || hasMult);
    body = (
      <>
        {wrapProduct || wrapAddends ? (
          <span className="points-breakdown-paren">(</span>
        ) : null}
        {addends.map((term, index) => (
          <React.Fragment key={`add-${index}`}>
            {index > 0 ? ' + ' : null}
            <Term value={term.value} icons={term.icons} inventory={inventory} />
          </React.Fragment>
        ))}
        {wrapAddends ? <span className="points-breakdown-paren">)</span> : null}
        {multiplier ? (
          <>
            {' × '}
            <MultiplierTerm multiplier={multiplier} inventory={inventory} />
          </>
        ) : null}
        {wrapProduct ? <span className="points-breakdown-paren">)</span> : null}
        {postMultiplierAddends.map((term, index) => (
          <React.Fragment key={`post-${index}`}>
            {' + '}
            <Term value={term.value} icons={term.icons} inventory={inventory} />
          </React.Fragment>
        ))}
        {' = '}
        <span className="points-breakdown-total">{breakdown.total}</span>
      </>
    );
  }

  return (
    <div className="points-breakdown" aria-label="Points calculation">
      {body}
    </div>
  );
}

export default PointsBreakdown;
